/**
 * Modal to confirm placeholder publishing.
 */
import type { OrgUnit } from '@amzn/journeys-common';
import { isEmpty, uniq } from 'es-toolkit/compat';
import {
  type PublishSlatePlaceholdersMutationResponse,
  type SlatePlaceholderPublishInput,
  usePublishSlatePlaceholdersMutation,
} from 'generated/graphql';
import { type FC, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { stylesheet } from 'typestyle';
import { analyzeSlateImageDimensions, checkImageElongated, extractSlateImageUrls } from './slate-dimension-utils';
import type { SlateFormValue } from './slates.types';

export type SlatePlaceholdersUploadModalProps = {
  isSlatePlaceholderModalOpen: boolean;
  slatesList: SlateFormValue[];
  asin: string;
  marketplaceId: number;
  productId: string;
  merchantId: string;
  ou: OrgUnit;
  handleModalClose(): void;
};

const css = stylesheet({
  dynamicWidthModal: {
    maxWidth: '90%',
  },
});

export const SlatePlaceholdersUploadModal: FC<SlatePlaceholdersUploadModalProps> = ({
  isSlatePlaceholderModalOpen,
  slatesList,
  asin,
  marketplaceId,
  productId,
  merchantId,
  ou,
  handleModalClose,
}) => {
  const [publishSlatePlaceholders, { loading }] = usePublishSlatePlaceholdersMutation();
  const [publishedResults, setPublishedResults] = useState<PublishSlatePlaceholdersMutationResponse>();
  const hasPublishedResults = !isEmpty(publishedResults?.succeeded) || !isEmpty(publishedResults?.failed);
  const variants = uniq(slatesList.filter((slate) => !slate.lastPublishedDate).map((slate) => slate.variant));

  if (!isSlatePlaceholderModalOpen) {
    return null;
  }

  const onSubmit = async () => {
    try {
      // Extract image URLs from slates for dimension analysis
      const imageUrls = extractSlateImageUrls(slatesList);

      // Analyze image dimensions if URLs are available and determine variant size
      const dimensions = await analyzeSlateImageDimensions(imageUrls);
      const variantsSize: string[] = dimensions.map(({ height, width }) => {
        const elongated = checkImageElongated(width, height);
        return elongated ? 'elongated' : 'square';
      });

      const slatePlaceholderPublishInput: SlatePlaceholderPublishInput = {
        asin,
        marketplaceId,
        merchantId,
        ou,
        productId,
        variants,
        variantsSize,
      };

      publishSlatePlaceholders({
        update: (_, mutationResult) => {
          if (!mutationResult.data) {
            return;
          }
          setPublishedResults(mutationResult.data.publishSlatePlaceholders);
          if (!isEmpty(mutationResult.data.publishSlatePlaceholders.failed)) {
            console.error('Failed slate placeholder uploads: ', mutationResult.data.publishSlatePlaceholders.failed);
          }
        },
        variables: {
          input: slatePlaceholderPublishInput,
        },
      });
    } catch (error) {
      console.error('Error analyzing image dimensions:', error);

      // Fallback to original behavior without dimensions
      const slatePlaceholderPublishInput: SlatePlaceholderPublishInput = {
        asin,
        marketplaceId,
        merchantId,
        ou,
        productId,
        variants,
      };

      publishSlatePlaceholders({
        update: (_, mutationResult) => {
          if (!mutationResult.data) {
            return;
          }
          setPublishedResults(mutationResult.data.publishSlatePlaceholders);
          if (!isEmpty(mutationResult.data.publishSlatePlaceholders.failed)) {
            console.error('Failed slate placeholder uploads: ', mutationResult.data.publishSlatePlaceholders.failed);
          }
        },
        variables: {
          input: slatePlaceholderPublishInput,
        },
      });
    }
  };
  const onCancel = () => {
    // On a cancel action, reset the results so the user can try again.
    setPublishedResults(undefined);
    handleModalClose();
  };
  return (
    <div>
      <Modal dialogClassName={css.dynamicWidthModal} onHide={handleModalClose} show={isSlatePlaceholderModalOpen}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Placeholders</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pb-4 pt-4">
          {loading ? (
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            // biome-ignore lint/style/noNestedTernary: legacy code
          ) : !hasPublishedResults ? (
            !isEmpty(slatesList) ? (
              <>
                <div className="pb-2">
                  <strong>
                    The following Slate variants have never been published to Media Central and will have placeholders uploaded for use with
                    Advanced Preview:
                  </strong>
                </div>
                <ul>
                  {variants.map((variant) => (
                    <li key={variant}>{variant}</li>
                  ))}
                </ul>
              </>
            ) : (
              <span>
                <strong>No Slate variants require placeholders to be published.</strong>
              </span>
            )
          ) : (
            <>
              {!isEmpty(publishedResults?.succeeded) && (
                <>
                  <div className="pb-2 text-success">
                    <strong>These placeholders published successfully:</strong>
                  </div>
                  <ul>
                    {publishedResults?.succeeded.map((variant) => (
                      <li key={variant}>{variant}</li>
                    ))}
                  </ul>
                </>
              )}
              {!isEmpty(publishedResults?.failed) && (
                <>
                  <div className="pb-2 text-danger">These placeholders failed to publish:</div>
                  <ul>
                    {publishedResults?.failed.map((variant) => (
                      <li key={variant}>{variant}</li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!hasPublishedResults && !loading ? (
            <>
              <Button data-cy="slate-placeholders-upload-modal-cancel" onClick={onCancel} variant="secondary">
                Cancel
              </Button>
              <Button data-cy="slate-placeholders-upload-modal-submit" onClick={onSubmit} variant="primary">
                Submit
              </Button>
            </>
          ) : (
            <Button data-cy="slate-placeholders-upload-modal-ok" onClick={onCancel} variant="primary">
              Ok
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};
